import React, { useState } from 'react';
import TripActionsMenu from '../actions/TripActionsMenu';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { formatDMY } from '../../utils/datetime';

const TripCard = ({ trips, handleEdit, handleStateUpdate, onDownloadDocs, onDownloadManifest }) => {
  const [loadingManifest, setLoadingManifest] = useState({});
  const u = value => String(value ?? '').toUpperCase();

  const statusColor = (status) => {
    switch (status) {
      case 'ongoing':   return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default:          return 'bg-gray-100 text-gray-600';
    }
  };

  const handleManifestClick = async (trip) => {
    setLoadingManifest(prev => ({ ...prev, [trip.id]: true }));
    await onDownloadManifest(trip);
    setLoadingManifest(prev => ({ ...prev, [trip.id]: false }));
  };

  return (
    <div className="grid gap-3 md:hidden">
      {trips.length === 0 && (
        <p className="text-center text-gray-500 py-10 text-sm">No trips found.</p>
      )}
      {trips.map((trip, idx) => (
        <div
          key={idx}
          className="bg-white shadow-sm border rounded-lg p-4"
        >
          <div className="flex justify-between items-start">
            {/* Left: code + metadata */}
            <div
              className="flex-1 cursor-pointer"
              onClick={() => handleEdit(trip)}
            >
              <div className="flex items-baseline gap-2">
                <p className="font-bold text-[#E91C24] text-sm">{u(trip.trip_code)}</p>
                <p className="text-xs text-gray-400">#{trip.id}</p>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{u(trip.branch?.name)}</p>
              <p className="text-xs text-gray-700 mt-1 font-medium">{u(trip.customer?.name)}</p>
              <p className="text-xs text-gray-700 mt-1 font-medium">{u(trip.client?.name)}</p>
            </div>

            {/* Right: status badge + actions */}
            <div className="flex flex-col items-end gap-2 shrink-0 ml-2">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor(trip.status)}`}>
                {u(trip.status)}
              </span>
              <TripActionsMenu
                onEdit={() => handleEdit(trip)}
                onStateUpdate={handleStateUpdate}
                onDownloadDocs={onDownloadDocs}
                onDownloadManifest={onDownloadManifest}
                trip={trip}
              />
            </div>
          </div>

          {/* Footer row: vehicle + dates + manifest download */}
          <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
            {trip.vehicle_number && (
              <div><span className="font-medium text-gray-600">Vehicle:</span> {u(trip.vehicle_number)}</div>
            )}
            {trip.driver_name && (
              <div><span className="font-medium text-gray-600">Driver:</span> {u(trip.driver_name)}</div>
            )}
            {trip.expected_pickup_date && (
              <div>
                <span className="font-medium text-gray-600">Pickup:</span>{' '}
                {formatDMY(trip.expected_pickup_date)}
              </div>
            )}
            {trip.expected_delivery_date && (
              <div>
                <span className="font-medium text-gray-600">Delivery:</span>{' '}
                {formatDMY(trip.expected_delivery_date)}
              </div>
            )}
            {/* Manifest download — spans full width */}
            <div className="col-span-2 mt-1">
              <button
                onClick={() => handleManifestClick(trip)}
                disabled={loadingManifest[trip.id]}
                className="flex items-center gap-1.5 text-orange-500 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {loadingManifest[trip.id] ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <ArrowDownTrayIcon className="w-4 h-4" />
                )}
                {loadingManifest[trip.id] ? 'Downloading...' : 'Download Manifest'}
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TripCard;
